import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  Award,
  Building2,
  Calendar,
  CheckCircle,
  Edit2,
  KeyRound,
  Loader2,
  Mail,
  Phone,
  Save,
  Shield,
  Stethoscope,
  User,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import {
  getProfile,
  updateProfile,
  changePassword,
  requestEmailChange,
  verifyEmailChange,
  type ProfileData,
} from '../services/profileService';

const PHONE_REGEX = /^[+]?[\d\s\-()]{7,20}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const requestError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return requestError?.response?.data?.message || requestError?.message || fallback;
};

const formatRoleDisplay = (role: string | null | undefined): string => {
  if (!role) return 'User';
  return role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const validateNewPassword = (password: string): string | null => {
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain a number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain a special character';
  return null;
};

const Field: React.FC<{
  label: string;
  htmlFor?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, error, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-neutral-700">
      {label}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
  </div>
);

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Departments for optional department selection (if applicable)
  const [departments] = useState<Array<{ id: number; name: string }>>([]);

  // Draft editable fields
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedDepartmentId, setEditedDepartmentId] = useState<string>('');
  const [editedGender, setEditedGender] = useState<'Male' | 'Female' | 'Other' | ''>('');
  const [editedQualifications, setEditedQualifications] = useState('');
  const [editedProfileSummary, setEditedProfileSummary] = useState('');
  const [editedClinicalSummary, setEditedClinicalSummary] = useState('');

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  // Email change & OTP states
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState<string | null>(null);

  // Countdown timer for OTP resend cooldown
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Change password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordSubmitError, setPasswordSubmitError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Fetch initial profile
  useEffect(() => {
    let isCurrentRequest = true;

    const fetchInitialData = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const profileRes = await getProfile();
        if (isCurrentRequest) {
          setProfileData(profileRes);
        }
      } catch (err) {
        if (isCurrentRequest) {
          setLoadError(getErrorMessage(err, 'Failed to load profile details'));
        }
      } finally {
        if (isCurrentRequest) setIsLoading(false);
      }
    };

    void fetchInitialData();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const populateDrafts = (data: ProfileData | null) => {
    if (!data) return;
    setEditedName(data.name || '');
    setEditedEmail(data.email || '');
    setEditedPhone(data.phone || data.contact || '');
    setEditedDepartmentId(data.departmentId ? String(data.departmentId) : '');
    setEditedGender(
      (data.gender as 'Male' | 'Female' | 'Other') || ''
    );
    setEditedQualifications(data.qualifications || '');
    setEditedProfileSummary(data.profileSummary || '');
    setEditedClinicalSummary(data.clinicalSummary || '');
  };

  useEffect(() => {
    if (!profileData || isEditMode) return;
    populateDrafts(profileData);
  }, [profileData, isEditMode]);

  const fallbackProfile: ProfileData | null = user
    ? {
        id: user.id || user.userId,
        name: user.name,
        email: user.email,
        role: user.role,
        roleName: user.roleName || user.role,
        departmentId: user.department_id,
        active: true,
      }
    : null;

  const displayData = profileData || fallbackProfile;
  const roleDisplay = displayData?.roleName || formatRoleDisplay(displayData?.role);
  const isDoctor = Boolean(displayData?.isDoctor);

  // Identify admin users (Super Admin or roles with admin)
  const roleNormalized = (displayData?.role || user?.role || '').toLowerCase();
  const roleNameNormalized = (displayData?.roleName || user?.roleName || '').toLowerCase();
  const isAdmin = Boolean(
    user?.isSuperAdmin ||
    roleNormalized.includes('admin') ||
    roleNameNormalized.includes('admin')
  );

  const handleEdit = () => {
    populateDrafts(displayData);
    setProfileErrors({});
    setLoadError(null);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    populateDrafts(displayData);
    setProfileErrors({});
    setLoadError(null);
    setIsEditMode(false);
    setIsEmailModalOpen(false);
    setDevOtp(null);
    setOtp('');
    setOtpError(null);
  };

  const handleCancelEmailChange = () => {
    setIsEmailModalOpen(false);
    setOtp('');
    setOtpError(null);
    setDevOtp(null);
    if (displayData?.email) {
      setEditedEmail(displayData.email);
    }
    setIsEditMode(false);
    toast('Email change cancelled. Other profile changes were saved.', { icon: 'ℹ️' });
  };

  const handleSave = async () => {
    if (!displayData) return;

    const normalizedName = editedName.trim();
    const normalizedEmail = editedEmail.trim().toLowerCase();
    const normalizedPhone = editedPhone.trim();
    const normalizedQualifications = editedQualifications.trim();
    const normalizedProfileSummary = editedProfileSummary.trim();
    const normalizedClinicalSummary = editedClinicalSummary.trim();

    const errors: Record<string, string> = {};

    if (!normalizedName) {
      errors.name = 'Full name is required';
    } else if (normalizedName.length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    }

    if (!normalizedEmail) {
      errors.email = 'Email address is required';
    } else if (!EMAIL_REGEX.test(normalizedEmail)) {
      errors.email = 'Enter a valid email address';
    }

    if (normalizedPhone && !PHONE_REGEX.test(normalizedPhone)) {
      errors.phone = 'Enter a valid phone number (e.g. +1234567890)';
    }

    setProfileErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    setLoadError(null);

    const emailChanged = normalizedEmail !== (displayData.email || '').toLowerCase();

    try {
      const payload: Parameters<typeof updateProfile>[0] = {
        name: normalizedName,
        phone: normalizedPhone,
      };

      // Only pass department_id for non-admin users
      if (!isAdmin && editedDepartmentId) {
        payload.department_id = Number(editedDepartmentId);
      }

      if (isDoctor) {
        payload.gender = editedGender || null;
        payload.qualifications = normalizedQualifications || null;
        payload.profileSummary = normalizedProfileSummary || null;
        payload.clinicalSummary = normalizedClinicalSummary || null;
      }

      const updated = await updateProfile(payload);
      setProfileData(updated);

      // Update auth context so sidebar immediately displays updated name
      if (updateUser) {
        updateUser({
          name: updated.name,
        });
      }

      if (emailChanged) {
        // Request OTP to the new email address
        const reqResult = await requestEmailChange(normalizedEmail);
        setPendingEmail(normalizedEmail);
        setOtp('');
        setOtpError(null);
        if (reqResult.otp) {
          setDevOtp(reqResult.otp);
        }
        setResendCooldown(30);
        setIsEmailModalOpen(true);
        toast.success(reqResult.message || `Verification code sent to ${normalizedEmail}`);
      } else {
        populateDrafts(updated);
        setIsEditMode(false);
        toast.success('Profile updated successfully');
      }
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to save profile changes');
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerifyOtp = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    const cleanOtp = otp.trim();
    if (!cleanOtp || cleanOtp.length !== 6) {
      setOtpError('Please enter the 6-digit verification code');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const updated = await verifyEmailChange(cleanOtp);
      setProfileData(updated);
      populateDrafts(updated);

      // Keep AuthContext in sync so user state and localStorage reflect the new email immediately
      if (updateUser) {
        updateUser({
          email: updated.email,
          name: updated.name,
        });
      }

      setIsEmailModalOpen(false);
      setIsEditMode(false);
      setDevOtp(null);
      setOtp('');
      toast.success('Email updated successfully! You can now use this email to log in.');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to verify verification code');
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingEmail || resendCooldown > 0 || isResendingOtp) return;

    setIsResendingOtp(true);
    setOtpError(null);

    try {
      const reqResult = await requestEmailChange(pendingEmail);
      if (reqResult.otp) {
        setDevOtp(reqResult.otp);
      }
      setResendCooldown(30);
      toast.success(reqResult.message || `A new verification code was sent to ${pendingEmail}`);
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to resend verification code');
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: Record<string, string> = {};

    if (!currentPassword) errors.currentPassword = 'Current password is required';
    const passwordValidationError = validateNewPassword(newPassword);
    if (passwordValidationError) errors.newPassword = passwordValidationError;
    if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (currentPassword && currentPassword === newPassword) {
      errors.newPassword = 'New password must be different from the current password';
    }

    setPasswordErrors(errors);
    setPasswordSubmitError(null);
    if (Object.keys(errors).length > 0) return;

    setIsChangingPassword(true);

    try {
      const result = await changePassword(currentPassword, newPassword, confirmPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(result.message || 'Password changed successfully');
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to change password');
      setPasswordSubmitError(message);
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading && !displayData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-neutral-500 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3 text-danger-600">
            <AlertCircle className="h-5 w-5" />
            <p className="font-semibold">Unable to load profile</p>
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            {loadError || 'Profile information is currently unavailable.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Top Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Profile</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Manage your personal account and professional information.
          </p>
        </div>
        {!isEditMode && (
          <button
            type="button"
            onClick={handleEdit}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <Edit2 className="h-4 w-4" />
            Edit profile
          </button>
        )}
      </div>

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3.5 text-sm text-danger-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {/* Main Profile Card */}
      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm overflow-hidden">
        {/* User Card Top Banner */}
        <div className="border-b border-neutral-200 bg-gradient-to-r from-neutral-50 via-white to-primary-50/20 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-primary-100 border-2 border-primary-200 shadow-sm">
                <User className="h-10 w-10 text-primary-600" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold text-neutral-900">
                  {displayData.name}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-700 border border-neutral-200">
                    <Shield className="h-3.5 w-3.5 text-primary-600" />
                    <span>{roleDisplay}</span>
                  </span>
                  {/* Department badge - not shown for admin */}
                  {displayData.departmentName && !isAdmin && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 border border-primary-100">
                      <Building2 className="h-3.5 w-3.5 text-primary-600" />
                      <span>{displayData.departmentName}</span>
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      displayData.active
                        ? 'bg-success-50 text-success-600 border border-success-200'
                        : 'bg-danger-50 text-danger-600 border border-danger-200'
                    }`}
                  >
                    {displayData.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="space-y-6 p-5 sm:p-6">
          <div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1 flex items-center gap-2">
              <User className="h-4 w-4 text-primary-600" />
              Account & Personal Information
            </h3>
            <p className="text-xs text-neutral-500 mb-4">
              Registered credentials and contact details.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Full Name */}
            {isEditMode ? (
              <Field label="Full Name" htmlFor="profile-name" error={profileErrors.name}>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    id="profile-name"
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter your name"
                  />
                </div>
              </Field>
            ) : (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Full Name</p>
                <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                  <User className="h-4 w-4 text-neutral-400" />
                  <span>{displayData.name || 'Not specified'}</span>
                </div>
              </div>
            )}

            {/* Email Address */}
            {isEditMode ? (
              <Field label="Email Address" htmlFor="profile-email" error={profileErrors.email}>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    id="profile-email"
                    type="email"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter email address"
                  />
                </div>
                <p className="mt-1 text-[11px] text-neutral-500">
                  Changing your email requires entering a 6-digit verification code sent to the new address before it takes effect.
                </p>
              </Field>
            ) : (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Email Address</p>
                <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <span>{displayData.email}</span>
                </div>
                <p className="mt-1 text-[11px] text-neutral-400">
                  Primary login identifier.
                </p>
              </div>
            )}

            {/* Username (if present) */}
            {displayData.username && (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Username</p>
                <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                  <span className="text-neutral-400 font-mono text-sm">@</span>
                  <span>{displayData.username}</span>
                </div>
              </div>
            )}

            {/* Phone Number */}
            {isEditMode ? (
              <Field label="Phone Number" htmlFor="profile-phone" error={profileErrors.phone}>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    id="profile-phone"
                    type="text"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. +1234567890"
                  />
                </div>
              </Field>
            ) : (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Phone Number</p>
                <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                  <Phone className="h-4 w-4 text-neutral-400" />
                  <span>{displayData.phone || displayData.contact || 'Not specified'}</span>
                </div>
              </div>
            )}

            {/* Department (Hidden completely for Admins) */}
            {!isAdmin && (
              isEditMode ? (
                <Field label="Department" htmlFor="profile-department">
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <select
                      id="profile-department"
                      value={editedDepartmentId}
                      onChange={(e) => setEditedDepartmentId(e.target.value)}
                      className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select department (optional)</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              ) : (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Department</p>
                  <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                    <Building2 className="h-4 w-4 text-neutral-400" />
                    <span>{displayData.departmentName || 'Not assigned'}</span>
                  </div>
                </div>
              )
            )}

            {/* Role */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Assigned Role</p>
              <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                <Shield className="h-4 w-4 text-neutral-400" />
                <span>{roleDisplay}</span>
              </div>
            </div>

            {/* Account Status */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Account Status</p>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                    displayData.active
                      ? 'bg-success-50 text-success-600'
                      : 'bg-danger-50 text-danger-600'
                  }`}
                >
                  {displayData.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Registration Date */}
            {displayData.createdAt && (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Member Since</p>
                <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                  <Calendar className="h-4 w-4 text-neutral-400" />
                  <span>
                    {new Date(displayData.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Professional Information Section (for Doctors / Clinical Staff) */}
          {isDoctor && (
            <div className="border-t border-neutral-200 pt-6">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-neutral-900 flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-primary-600" />
                  Professional Information
                </h3>
                <p className="text-xs text-neutral-500">
                  Clinical details, medical registration, and practice summaries.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {/* Medical Registration Number */}
                <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">
                    Medical Registration Number
                  </p>
                  <div className="flex items-center gap-2 text-neutral-900 font-medium text-sm">
                    <Award className="h-4 w-4 text-primary-600" />
                    <span className="font-mono">{displayData.registrationNumber || 'Not specified'}</span>
                  </div>
                </div>

                {/* Gender */}
                {isEditMode ? (
                  <Field label="Gender" htmlFor="profile-gender">
                    <select
                      id="profile-gender"
                      value={editedGender}
                      onChange={(e) =>
                        setEditedGender(e.target.value as 'Male' | 'Female' | 'Other' | '')
                      }
                      className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </Field>
                ) : (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Gender</p>
                    <p className="text-sm font-medium text-neutral-900">{displayData.gender || 'Not specified'}</p>
                  </div>
                )}

                {/* Qualifications */}
                {isEditMode ? (
                  <Field label="Qualifications" htmlFor="profile-qualifications">
                    <input
                      id="profile-qualifications"
                      type="text"
                      value={editedQualifications}
                      onChange={(e) => setEditedQualifications(e.target.value)}
                      placeholder="e.g. MBBS, MD, DM (Clinical Hematology)"
                      className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    />
                  </Field>
                ) : (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Qualifications</p>
                    <p className="text-sm font-medium text-neutral-900">
                      {displayData.qualifications || 'Not specified'}
                    </p>
                  </div>
                )}

                {/* Clinical Reviewer Info */}
                <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">MDT Reviewer Role</p>
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-900">
                    {displayData.is_reviewer ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                        <CheckCircle className="h-3.5 w-3.5" />
                        Senior Reviewer (Authorized to approve MDT reports)
                      </span>
                    ) : displayData.reviewerName ? (
                      <span className="text-neutral-700">
                        Supervisor: <span className="font-semibold">{displayData.reviewerName}</span>
                      </span>
                    ) : (
                      <span className="text-neutral-500">Standard Clinician</span>
                    )}
                  </div>
                </div>

                {/* Profile Summary */}
                <div className="md:col-span-2">
                  {isEditMode ? (
                    <Field label="Profile Summary" htmlFor="profile-summary">
                      <textarea
                        id="profile-summary"
                        rows={3}
                        value={editedProfileSummary}
                        onChange={(e) => setEditedProfileSummary(e.target.value)}
                        placeholder="Brief overview of clinical focus and background..."
                        className="w-full rounded-lg border border-neutral-300 bg-white p-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                      />
                    </Field>
                  ) : (
                    <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Profile Summary</p>
                      <p className="text-sm text-neutral-800 whitespace-pre-line">
                        {displayData.profileSummary || 'No summary provided.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Clinical Summary */}
                <div className="md:col-span-2">
                  {isEditMode ? (
                    <Field label="Clinical Summary" htmlFor="clinical-summary">
                      <textarea
                        id="clinical-summary"
                        rows={3}
                        value={editedClinicalSummary}
                        onChange={(e) => setEditedClinicalSummary(e.target.value)}
                        placeholder="Details of specializations, diagnostic expertise, or treatment focus..."
                        className="w-full rounded-lg border border-neutral-300 bg-white p-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                      />
                    </Field>
                  ) : (
                    <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Clinical Summary</p>
                      <p className="text-sm text-neutral-800 whitespace-pre-line">
                        {displayData.clinicalSummary || 'No clinical summary provided.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action buttons at bottom of profile section when editing */}
          {isEditMode && (
            <div className="flex flex-col-reverse gap-3 border-t border-neutral-200 pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition hover:bg-neutral-50 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSaving ? 'Saving changes...' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Change Password Card */}
      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-neutral-200 p-5 sm:p-6 bg-neutral-50/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Change Password</h2>
              <p className="text-sm text-neutral-500">
                Update your login password. Choose a strong, unique password.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-5 p-5 sm:p-6" noValidate>
          {passwordSubmitError && (
            <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{passwordSubmitError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <Field label="Current Password" htmlFor="current-password" error={passwordErrors.currentPassword}>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>

            <Field label="New Password" htmlFor="new-password" error={passwordErrors.newPassword}>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>

            <Field label="Confirm New Password" htmlFor="confirm-password" error={passwordErrors.confirmPassword}>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isChangingPassword}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
            >
              {isChangingPassword ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              {isChangingPassword ? 'Updating password...' : 'Update password'}
            </button>
          </div>
        </form>
      </section>

      {/* Email Verification Modal */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">Verify New Email</h3>
                  <p className="text-xs text-neutral-500">Confirm your email address update</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEmailChange}
                disabled={isVerifyingOtp}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleVerifyOtp} className="mt-5 space-y-4">
              <p className="text-sm text-neutral-600 leading-relaxed">
                A 6-digit verification code was sent to{' '}
                <span className="font-semibold text-neutral-900">{pendingEmail}</span>.
                Enter the code below to complete changing your email.
              </p>

              <div>
                <label htmlFor="email-otp-input" className="block text-sm font-medium text-neutral-700 mb-1">
                  6-Digit Verification Code
                </label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    id="email-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(val);
                      if (otpError) setOtpError(null);
                    }}
                    placeholder="••••••"
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-center font-mono text-lg tracking-[0.3em] text-neutral-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>
                {otpError && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-danger-600">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>{otpError}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isResendingOtp || isVerifyingOtp}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-neutral-400 disabled:cursor-not-allowed transition"
                >
                  {isResendingOtp
                    ? 'Resending...'
                    : resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : 'Resend code'}
                </button>
                <span className="text-xs text-neutral-400">Code valid for 10 minutes</span>
              </div>

              <div className="flex items-center justify-end gap-2.5 border-t border-neutral-100 pt-4">
                <button
                  type="button"
                  onClick={handleCancelEmailChange}
                  disabled={isVerifyingOtp}
                  className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isVerifyingOtp || otp.length !== 6}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition disabled:opacity-50"
                >
                  {isVerifyingOtp ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Verify & Update</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dev/QA OTP badge — only visible in non-production */}
      {devOtp && process.env.NODE_ENV !== 'production' && (
        <div className="fixed bottom-4 right-4 z-50 bg-neutral-900/90 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-mono backdrop-blur-sm">
          <span className="opacity-60">Dev OTP:</span>{' '}
          <span className="font-bold tracking-widest">{devOtp}</span>
        </div>
      )}
    </div>
  );
};

export default Profile;
